// ============================================================
// Header.jsx
//
// Role-based navigation header for the ProDev platform.
//
// Navigation logic:
//   - Not logged in        → shows E-PORTAL (signin link)
//   - Logged in, no access → shows USER DASHBOARD only
//   - Logged in, courseAccess: true → shows COURSE HUB + SIGN OUT
//   - Admin                → shows ADMIN PANEL + COURSE HUB + SIGN OUT
//
// The DASHBOARD link in authNav adapts based on the user's
// courseAccess and role so users always land in the right place.
//
// Vision & Mission links are added to:
//   - mainNav array (mobile drawer)
//   - Desktop nav links (between the badge and CTA button)
// ============================================================

import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

import {
    AppBar, Toolbar, IconButton, Typography, Drawer,
    List, ListItemButton, ListItemIcon, ListItemText,
    Box, Divider, styled, Button, Tooltip,
} from "@mui/material";

// ── Icons ────────────────────────────────────────────────────
import MenuIcon from "@mui/icons-material/Menu";
import VisibilityIcon from "@mui/icons-material/Visibility";       // Vision page
import TrackChangesIcon from "@mui/icons-material/TrackChanges";   // Mission page
import PsychologyIcon from "@mui/icons-material/Psychology";       // 🆕 AI Mentor Icon
import { HiPencilSquare } from "react-icons/hi2";
import {
    FaHome, FaInfo, FaEuroSign, FaSignInAlt,
    FaSignOutAlt, FaBookmark, FaUserShield,
} from "react-icons/fa";
import { IoIosSchool } from "react-icons/io";
import { FaPeopleGroup } from "react-icons/fa6";
import { MdDashboard } from "react-icons/md";

// ── Internal ─────────────────────────────────────────────────
import { useNotification } from "../auth/NotificationProvider";
import { useAI } from "../context/AIProvider"; // 🆕 AI Context Hook
import ProdevConsult from "../assets/images/ProdevConsult.jpg";

// ============================================================
//  BRAND CONSTANTS
//  Single source of truth — change once, updates everywhere.
// ============================================================
const BRAND = {
    dark:     "#100720",
    gold:     "#E5C95F",
    goldHover:"#F0D670",
    goldFaint:"rgba(229,201,95,0.1)",
    goldBorder:"rgba(229,201,95,0.2)",
    dimWhite: "rgba(255,255,255,0.7)",
    divider:  "rgba(255,255,255,0.1)",
};

// ============================================================
//  STYLED COMPONENTS
//  Define all visual building blocks here — not inside JSX.
// ============================================================

/**
 * HeaderBar
 * Fixed MUI AppBar with the brand dark background.
 * boxShadow gives a subtle depth effect on scroll.
 */
const HeaderBar = styled(AppBar)(() => ({
    backgroundColor: BRAND.dark,
    color: "#FFFFFF",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    padding: "2px 0",
}));

/**
 * BadgeTag
 * Gold pill used for the "ADAPTIVE LEARNING SYSTEM" centre badge
 * and the drawer version tag.
 */
const BadgeTag = styled(Box)({
    color: BRAND.gold,
    padding: "4px 14px",
    borderRadius: "100px",
    fontSize: "0.75rem",
    fontWeight: 800,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
});

/**
 * StyledDrawer
 * Inner wrapper for the right-side navigation drawer.
 * Fixed width of 280px — enough for readable nav labels.
 */
const StyledDrawer = styled(Box)({
    width: 280,
    height: "112%",
    backgroundColor: BRAND.dark,
    color: "#FFFFFF",
    paddingTop: "10px",
});

/**
 * DesktopNavLink
 * Text-only nav link shown in the top bar on md+ screens.
 * Receives an `active` prop (boolean) — highlights the current page.
 *
 * Usage: <DesktopNavLink component={Link} to="/vision" active={1}>Vision</DesktopNavLink>
 * Note: MUI styled components need numeric 1/0 for boolean props
 * to avoid React DOM warnings — hence `active={isActive ? 1 : 0}`.
 */
const DesktopNavLink = styled(Button)(({ active }) => ({
    fontFamily: "'Poppins', sans-serif",
    fontWeight: active ? 700 : 500,
    fontSize: "0.78rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: active ? BRAND.gold : BRAND.dimWhite,
    padding: "6px 12px",
    borderRadius: "8px",
    minWidth: "auto",
    borderBottom: active ? `2px solid ${BRAND.gold}` : "2px solid transparent",
    transition: "color 0.2s ease, border-bottom-color 0.2s ease, background 0.2s ease",
    "&:hover": {
        color: BRAND.gold,
        backgroundColor: BRAND.goldFaint,
        borderBottomColor: BRAND.gold,
    },
}));

// ============================================================
//  MAIN COMPONENT
// ============================================================

/**
 * Header
 * Receives `user` from App.jsx (Firebase Auth user object or null).
 * Fetches Firestore profile to determine role and courseAccess,
 * then builds navigation arrays accordingly.
 *
 * Props:
 * user — Firebase Auth user object or null
 */
export default function Header({ user }) {
    const { notify }      = useNotification();
    const { setSidebarOpen } = useAI(); // 🆕 Destructure AI toggle function
    const location        = useLocation(); // Used to highlight the active nav link
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const auth            = getAuth();

    // ── User profile state ───────────────────────────────────
    // Stores role and courseAccess fetched from Firestore.
    // null = not yet fetched or not logged in.
    const [userProfile, setUserProfile] = useState(null);

    // ============================================================
    //  FETCH USER PROFILE
    //  Runs whenever the auth user object changes (login/logout).
    //  Reads from Firestore "users" collection using the user's UID.
    // ============================================================
    useEffect(() => {
        const fetchProfile = async () => {
            if (!user) {
                setUserProfile(null); // Clear profile on sign out
                return;
            }
            try {
                const snap = await getDoc(doc(db, "users", user.uid));
                if (snap.exists()) {
                    setUserProfile(snap.data());
                }
            } catch (err) {
                console.error("Header: failed to fetch user profile", err);
            }
        };

        fetchProfile();
    }, [user]);

    // ============================================================
    //  HELPERS
    // ============================================================

    /** Toggles the right-side drawer open/closed */
    const toggleDrawer = () => setIsDrawerOpen((prev) => !prev);

    /**
     * handleSignOut
     * Signs the user out of Firebase Auth, shows a notification,
     * then redirects to the home page via a hard redirect.
     * We use window.location.href instead of navigate() here
     * so that all in-memory state is fully cleared on sign out.
     */
    const handleSignOut = () => {
        signOut(auth)
            .then(() => {
                notify("Successfully signed out.", "info");
                window.location.href = "/";
            })
            .catch((error) => notify(error.message, "error"));
    };

    // ── Role flags ────────────────────────────────────────────
    // Derived booleans — computed once and reused throughout the JSX.
    const isAdmin         = userProfile?.role === "admin";
    const hasCourseAccess = userProfile?.courseAccess === true;

    // ============================================================
    //  NAV DEFINITIONS
    //  All nav items live here as data arrays.
    //  The JSX below just maps over them — no hardcoded link JSX.
    // ============================================================

    /**
     * mainNav
     * Always visible to everyone, regardless of auth state.
     * These are the public-facing pages of the platform.
     *
     * Vision and Mission are included here so all users can
     * understand the platform's purpose and identity.
     * highlight: true renders the item with a gold background.
     */
    const mainNav = [
        { label: "HOME",        icon: <FaHome />,             to: "/home"                  },
        { label: "ABOUT",       icon: <FaInfo />,             to: "/about-prodev"          },
        { label: "VISION",      icon: <VisibilityIcon sx={{ fontSize: 16 }} />, to: "/vision"   },
        { label: "MISSION",     icon: <TrackChangesIcon sx={{ fontSize: 16 }} />, to: "/mission" },
        { label: "PRICING",     icon: <FaEuroSign />,         to: "/pricing-page"          },
        { label: "COURSES",     icon: <IoIosSchool />,        to: "/course-page"           },
        { label: "PARTNERSHIP", icon: <FaPeopleGroup />,      to: "/partnership-form"      },
        {
            label: "APPLICATION",
            icon: <HiPencilSquare />,
            to: "/plan-application-page",
            highlight: true, // Gold-tinted highlight in the drawer
        },
    ];

    /**
     * desktopNav
     * Subset of mainNav shown as text links in the top bar on
     * md+ screens (between the centre badge and the CTA button).
     * Keeping this short prevents the toolbar from overflowing.
     *
     * Vision and Mission are the primary additions here so they
     * are always discoverable without opening the drawer.
     */
    const desktopNav = [
        { label: "Home",        to: "/home"           },
        { label: "About",       to: "/about-prodev"   },
        { label: "Vision",      to: "/vision"         },
        { label: "Mission",     to: "/mission"        },
        { label: "Pricing",     to: "/pricing-page"   },
    ];

    /**
     * authNav
     * Changes based on auth state and user role.
     * * 🔥 ARCHITECT UPDATE:
     * AI MENTOR entries now omit the 'to' property to prevent
     * navigation and use 'action' to trigger the sidebar toggle.
     */
    const authNav = !user
        ? [
            // Not logged in — show sign in link only
            {
                label: "E-PORTAL",
                icon: <FaSignInAlt />,
                to: "/signin",
            },
        ]
        : isAdmin
            ? [
                // Admin — full access
                { label: "ADMIN PANEL", icon: <FaUserShield />, to: "/admin" },
                // 🆕 AI Action - No 'to' property to prevent redirection
                { label: "AI MENTOR", icon: <PsychologyIcon />, action: () => { setSidebarOpen(true); toggleDrawer(); } },
                { label: "COURSE HUB",  icon: <FaBookmark />,   to: "/course-hub"        },
                { label: "SIGN OUT",    icon: <FaSignOutAlt />, action: handleSignOut     },
            ]
            : hasCourseAccess
                ? [
                    // Regular user with course access
                    { label: "MY DASHBOARD", icon: <MdDashboard />, to: "/user-dashboard" },
                    // 🆕 AI Action - No 'to' property to prevent redirection
                    { label: "AI MENTOR", icon: <PsychologyIcon />, action: () => { setSidebarOpen(true); toggleDrawer(); } },
                    { label: "COURSE HUB",   icon: <FaBookmark />,  to: "/course-hub"     },
                    { label: "SIGN OUT",     icon: <FaSignOutAlt />, action: handleSignOut },
                ]
                : [
                    // Regular user — no course access yet
                    { label: "MY DASHBOARD", icon: <MdDashboard />, to: "/user-dashboard" },
                    // 🆕 AI Action - No 'to' property to prevent redirection
                    { label: "AI MENTOR", icon: <PsychologyIcon />, action: () => { setSidebarOpen(true); toggleDrawer(); } },
                    { label: "SIGN OUT",     icon: <FaSignOutAlt />, action: handleSignOut },
                ];

    // ============================================================
    //  RENDER
    // ============================================================
    return (
        <>
            <HeaderBar position="fixed">
                <Toolbar sx={{ justifyContent: "space-between" }}>

                    {/* ── LEFT: Logo ────────────────────────────────── */}
                    <Box
                        component={Link}
                        to="/"
                        sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}
                    >
                        <Box
                            component="img"
                            src={ProdevConsult}
                            alt="ProDev Logo"
                            sx={{ height: "85px", width: "85px", borderRadius: "50%" }}
                        />
                    </Box>

                    {/* ── MIDDLE: Badge + Desktop Nav Links ─────────── */}
                    <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.5 }}>

                        {/* Centre badge — hidden on small screens */}
                        <BadgeTag sx={{ display: { xs: "none", sm: "block" }, mr: 1 }}>
                            ADAPTIVE LEARNING SYSTEM
                        </BadgeTag>

                        {/*
                         * Desktop nav links — mapped from desktopNav array.
                         * useLocation().pathname is compared to each link's `to`
                         * so the active page is highlighted in gold.
                         * active prop: pass as number (1/0) to avoid MUI DOM warning.
                         */}
                        {desktopNav.map((item, i) => (
                            <DesktopNavLink
                                key={i}
                                component={Link}
                                to={item.to}
                                active={location.pathname === item.to ? 1 : 0}
                            >
                                {item.label}
                            </DesktopNavLink>
                        ))}
                    </Box>

                    {/* ── RIGHT: CTA + Hamburger ────────────────────── */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>

                        {/* 🆕 Desktop AI Mentor Toggle ────────────────── */}
                        {user && (
                            <Tooltip title="AI Mentor">
                                <IconButton
                                    onClick={() => setSidebarOpen(true)}
                                    sx={{
                                        display: { xs: "none", md: "flex" },
                                        color: BRAND.gold,
                                        backgroundColor: BRAND.goldFaint,
                                        "&:hover": { backgroundColor: BRAND.goldBorder },
                                    }}
                                >
                                    <PsychologyIcon />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Apply button — shown to logged-out users only on md+ */}
                        {!user && (
                            <Button
                                component={Link}
                                to="/plan-application-page"
                                variant="contained"
                                size="small"
                                startIcon={<HiPencilSquare />}
                                sx={{
                                    display: { xs: "none", md: "flex" },
                                    bgcolor: BRAND.gold,
                                    color: BRAND.dark,
                                    fontWeight: 800,
                                    borderRadius: "8px",
                                    "&:hover": { bgcolor: BRAND.goldHover },
                                }}
                            >
                                Apply
                            </Button>
                        )}

                        {/* Dashboard shortcut — shown to logged-in users only on md+ */}
                        {user && (
                            <Button
                                component={Link}
                                to={isAdmin ? "/admin-signup-page" : "/user-dashboard"}
                                variant="outlined"
                                size="small"
                                startIcon={isAdmin ? <FaUserShield /> : <MdDashboard />}
                                sx={{
                                    display: { xs: "none", md: "flex" },
                                    borderColor: BRAND.gold,
                                    color: BRAND.gold,
                                    fontWeight: 700,
                                    borderRadius: "8px",
                                    "&:hover": {
                                        borderColor: BRAND.goldHover,
                                        backgroundColor: BRAND.goldFaint,
                                    },
                                }}
                            >
                                {isAdmin ? "Admin Panel" : "My Dashboard"}
                            </Button>
                        )}

                        {/* Hamburger — always visible, opens the right-side drawer */}
                        <IconButton onClick={toggleDrawer} edge="end">
                            <MenuIcon sx={{ color: BRAND.gold, fontSize: "1.8rem" }} />
                        </IconButton>
                    </Box>
                </Toolbar>
            </HeaderBar>

            {/* ════════════════════════════════════════════════════
                NAVIGATION DRAWER
                Slides in from the right on hamburger click.
                Contains mainNav + authNav for all screen sizes.
            ════════════════════════════════════════════════════ */}
            <Drawer
                anchor="right"
                open={isDrawerOpen}
                onClose={toggleDrawer}
                PaperProps={{ sx: { border: "none" } }}
            >
                <StyledDrawer role="presentation">

                    {/* Drawer Header — title + version badge */}
                    <Box sx={{
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}>
                        <Typography variant="h6" fontWeight={800} sx={{ color: BRAND.gold }}>
                            Menu
                        </Typography>
                        <BadgeTag sx={{ fontSize: "0.6rem", px: 1 }}>v1.0</BadgeTag>
                    </Box>

                    {/* User role indicator — only shown when logged in */}
                    {user && userProfile && (
                        <Box sx={{ px: 2, pb: 1 }}>
                            <Box
                                sx={{
                                    px: 2, py: 1,
                                    borderRadius: "8px",
                                    backgroundColor: BRAND.goldFaint,
                                    border: `1px solid ${BRAND.goldBorder}`,
                                }}
                            >
                                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                                    Signed in as
                                </Typography>
                                <Typography variant="body2" fontWeight={700} sx={{ color: BRAND.gold }}>
                                    {userProfile.firstname
                                        ? `${userProfile.firstname} ${userProfile.lastname}`
                                        : userProfile.username || user.email
                                    }
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
                                    {isAdmin ? "Administrator" : hasCourseAccess ? "Active Member" : "Pending Access"}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <Divider sx={{ bgcolor: BRAND.divider, my: 1 }} />

                    <List sx={{ px: 1, mt: 1 }}>

                        {/* ── Main Navigation ───────────────────────────
                            Mapped from mainNav array defined above.
                            Vision and Mission are included in this array.
                            highlight: true renders with a gold background.
                        ─────────────────────────────────────────────── */}
                        {mainNav.map((item, index) => (
                            <ListItemButton
                                key={index}
                                component={Link}
                                to={item.to}
                                onClick={toggleDrawer}
                                sx={{
                                    borderRadius: "10px",
                                    mb: 0.5,
                                    // Active page — gold tint
                                    bgcolor: location.pathname === item.to
                                        ? BRAND.goldFaint
                                        : item.highlight
                                            ? "rgba(229,201,95,0.15)"
                                            : "transparent",
                                    "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        // Active or highlighted items use gold icon
                                        color: location.pathname === item.to || item.highlight
                                            ? BRAND.gold
                                            : BRAND.dimWhite,
                                        minWidth: 32,
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontWeight: location.pathname === item.to || item.highlight ? 800 : 500,
                                        fontSize: "0.95rem",
                                        color: location.pathname === item.to || item.highlight
                                            ? BRAND.gold
                                            : "#FFFFFF",
                                    }}
                                />
                                {/* Active page indicator dot */}
                                {location.pathname === item.to && (
                                    <Box
                                        sx={{
                                            width: 6, height: 6,
                                            borderRadius: "50%",
                                            backgroundColor: BRAND.gold,
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                            </ListItemButton>
                        ))}

                        <Divider sx={{ my: 2, bgcolor: BRAND.divider }} />

                        {/* ── Auth Navigation ───────────────────────────
                            Mapped from authNav array — changes based on
                            login state, role, and courseAccess flag.
                            SIGN OUT uses a red hover to signal danger.
                        ─────────────────────────────────────────────── */}
                        {authNav.map((item, index) => (
                            <ListItemButton
                                key={index}
                                // 🔥 Component becomes a 'div' if no 'to' is present
                                component={item.to ? Link : "div"}
                                to={item.to}
                                onClick={item.action || toggleDrawer}
                                sx={{
                                    borderRadius: "10px",
                                    mb: 0.5,
                                    "&:hover": {
                                        bgcolor: item.label === "SIGN OUT"
                                            ? "rgba(255,80,80,0.1)"
                                            : BRAND.goldFaint,
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        color: item.label === "SIGN OUT"
                                            ? "rgba(255,100,100,0.8)"
                                            : BRAND.gold,
                                        minWidth: 32,
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontWeight: 600,
                                        fontSize: "0.95rem",
                                        color: item.label === "SIGN OUT"
                                            ? "rgba(255,100,100,0.8)"
                                            : "#FFFFFF",
                                    }}
                                />
                            </ListItemButton>
                        ))}

                    </List>
                </StyledDrawer>
            </Drawer>

            {/* Toolbar spacer — prevents page content from hiding under the fixed AppBar */}
            <Toolbar />
        </>
    );
}
